// =============================================
// Pillar Cluster Context (A1 z planu SRO) — PER SEED MODEL (KOREKTA 2026-06-01)
//
// Pillar = hub silosa JEDNEGO seeda (NIE calej mapy).
// Pokrywa i linkuje spoki TEGO seeda (pillar.seed_id), encje przypisane do tych spokow.
//
// macro_statement zostaje jako kontekst nadrzedny mapy, ale czlony klastra = spoki seeda.
//
// Per MF-4 z planu: brak martwych linkow — wp_post_url tylko gdy is_published=true.
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { logDebug as _logDebugRaw } from "./contentLogger";

// P-D (faza P): kategoria "content-engine" zeby pillar context loggi widac w panelu LOGI.
const logDebug = (prefix: string, msg: string, data?: Record<string, unknown>) => _logDebugRaw(prefix, msg, data, "content-engine");

export interface PillarSpokeMember {
  node_id: string;
  title: string;
  slug: string;
  canonical_question: string | null;
  seed_id: string;
  seed_name: string | null;
  is_published: boolean;
  wp_post_url: string | null;
  publish_state: "published" | "ready_not_published" | "in_progress" | "placeholder";
}

export interface PillarClusterEntity {
  id: string;
  canonical_name: string;
  entity_type: string | null;
  aliases: string[];
  description: string | null;
}

export interface PillarClusterContext {
  pillar_node_id: string;
  map_id: string;
  map_name: string;
  macro_statement: string | null;
  // Per-seed scope
  seed_id: string;
  seed_name: string | null;
  seed_slug: string | null;
  cluster_member_entities: PillarClusterEntity[];
  cluster_member_spokes: PillarSpokeMember[];
  sample_spoke_titles: string[];
}

/**
 * Fetch pillar context — scope PER SEED (encje + spoki tylko TEGO seeda).
 * Idempotent — czysty SELECT.
 * Pillar identyfikowany przez (map_id + pillarNodeId) — preferuje explicit pillarNodeId.
 */
export async function getPillarClusterContext(
  mapId: string,
  pillarNodeId?: string,
): Promise<PillarClusterContext | null> {
  // 1. Pillar node — fetch z seed_id (kluczowe dla scope per seed)
  let pillarQuery = (supabase as any)
    .from("thematic_nodes")
    .select("id, title, slug, document_role, node_role, seed_id")
    .eq("map_id", mapId);

  if (pillarNodeId) {
    pillarQuery = pillarQuery.eq("id", pillarNodeId);
  } else {
    pillarQuery = pillarQuery.or("document_role.eq.pillar,node_role.eq.pillar");
  }

  const { data: pillarNodes } = await pillarQuery.limit(1);
  if (!pillarNodes || pillarNodes.length === 0) {
    logDebug("PillarContext", `Map ${mapId}: no pillar node found`);
    return null;
  }
  const pillarNode = pillarNodes[0];
  const pillarSeedId = pillarNode.seed_id as string | null;
  if (!pillarSeedId) {
    logDebug("PillarContext", `Pillar ${pillarNode.id} has no seed_id — scope ambiguous, abort`);
    return null;
  }

  // 2. Map info (macro_statement jako kontekst nadrzedny)
  const { data: mapData } = await (supabase as any)
    .from("thematic_maps")
    .select("id, name, macro_statement")
    .eq("id", mapId)
    .single();

  if (!mapData) return null;

  // 3. Seed info (name, slug — kluczowe dla defaultow modala i prompta)
  const { data: seedData } = await (supabase as any)
    .from("thematic_seeds")
    .select("id, name, slug")
    .eq("id", pillarSeedId)
    .single();

  // 4. Spoki TEGO seeda (NIE pillar, NIE inne seedy)
  // BUGFIX 2026-06-01: zrodlem is_published / wp_post_url jest thematic_node_content
  // (engine_stage='published' + c.wp_post_url), NIE thematic_nodes.matched_wp_post_id + JOIN wp_posts.
  // Pole matched_wp_post_id nalezy do feature "Istniejace wpisy" (matching zewnetrznych postow),
  // NIE do publikacji z panelu Content Engine.
  const { data: spokesData } = await (supabase as any)
    .from("thematic_nodes")
    .select(`
      id, title, slug, canonical_question, seed_id,
      primary_entity_id, required_entity_ids,
      document_role, node_role,
      thematic_seeds(name),
      thematic_node_content(engine_stage, wp_post_id, wp_post_url, published_at)
    `)
    .eq("map_id", mapId)
    .eq("seed_id", pillarSeedId)
    .neq("id", pillarNode.id);

  // 5. Zbierz unique entity IDs ze spokow TEGO seeda (primary + required)
  const entityIdSet = new Set<string>();
  for (const spoke of spokesData || []) {
    if (spoke.primary_entity_id) entityIdSet.add(spoke.primary_entity_id);
    const required = Array.isArray(spoke.required_entity_ids) ? spoke.required_entity_ids : [];
    for (const eid of required) {
      if (typeof eid === "string") entityIdSet.add(eid);
    }
  }

  // 6. Fetch encje TEGO seeda (po zebranych ID)
  let cluster_member_entities: PillarClusterEntity[] = [];
  if (entityIdSet.size > 0) {
    const { data: entitiesData } = await (supabase as any)
      .from("thematic_entities")
      .select("id, canonical_name, entity_type, aliases, description")
      .in("id", Array.from(entityIdSet));

    cluster_member_entities = (entitiesData || []).map((e: any) => ({
      id: e.id,
      canonical_name: e.canonical_name,
      entity_type: e.entity_type,
      aliases: Array.isArray(e.aliases) ? e.aliases : [],
      description: e.description,
    }));
  }

  // 7. Build cluster_member_spokes z publish state — source: thematic_node_content
  // (engine_stage='published' + c.wp_post_url to TRUE source of truth dla panelu)
  const cluster_member_spokes: PillarSpokeMember[] = (spokesData || []).map((spoke: any) => {
    const content = Array.isArray(spoke.thematic_node_content)
      ? spoke.thematic_node_content[0]
      : spoke.thematic_node_content;
    const stage = content?.engine_stage as string | undefined;
    const contentWpUrl = (content?.wp_post_url as string | null) ?? null;

    const isPublished = stage === "published" && Boolean(contentWpUrl);
    let publish_state: PillarSpokeMember["publish_state"];
    if (isPublished) publish_state = "published";
    else if (stage === "ready") publish_state = "ready_not_published";
    else if (stage && stage !== "not_started") publish_state = "in_progress";
    else publish_state = "placeholder";

    const seed = Array.isArray(spoke.thematic_seeds) ? spoke.thematic_seeds[0] : spoke.thematic_seeds;

    return {
      node_id: spoke.id,
      title: spoke.title,
      slug: spoke.slug,
      canonical_question: spoke.canonical_question,
      seed_id: spoke.seed_id,
      seed_name: seed?.name ?? null,
      is_published: isPublished,
      wp_post_url: isPublished ? contentWpUrl : null,
      publish_state,
    };
  });

  const sample_spoke_titles = cluster_member_spokes.slice(0, 10).map((s) => s.title);

  logDebug(
    "PillarContext",
    `Pillar ${pillarNode.id} (seed=${seedData?.name || pillarSeedId.slice(0, 8)}): ${cluster_member_spokes.length} spokes, ${cluster_member_entities.length} entities`,
  );

  return {
    pillar_node_id: pillarNode.id,
    map_id: mapId,
    map_name: mapData.name,
    macro_statement: mapData.macro_statement,
    seed_id: pillarSeedId,
    seed_name: seedData?.name ?? null,
    seed_slug: seedData?.slug ?? null,
    cluster_member_entities,
    cluster_member_spokes,
    sample_spoke_titles,
  };
}

/**
 * Wyciaga relatywny slug z pelnego URL (np. https://growthhacker.pl/foo/ -> /foo).
 * Zwraca null gdy URL invalid.
 * IDENTYCZNA konwencja jak spoke `draftGenerator.ts:538` (/${target_slug} z prefiksem).
 */
export function extractSlugFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(/\/([^/]+)\/?$/);
  return match ? `/${match[1]}` : null;
}

/**
 * Format pillar context jako template vars dla compilePromptSafe.
 * Wszystkie return strings (compilePrompt-safe).
 *
 * FAZA P v2 (2026-06-01): format cluster_member_slugs zmieniony na "- /slug — title (hub_to_spoke)"
 * identyczny ze spokowym (`/slug` z prefiksem). Cowork v4 prompty pasuja do tego formatu.
 */
export function formatPillarContextAsVars(
  ctx: PillarClusterContext,
): Record<string, string> {
  // Entities jako markdown list "- canonical_name (type): description"
  const entitiesStr = ctx.cluster_member_entities
    .map((e) => {
      const desc = e.description ? `: ${e.description.slice(0, 100)}` : "";
      const type = e.entity_type ? ` (${e.entity_type})` : "";
      return `- ${e.canonical_name}${type}${desc}`;
    })
    .join("\n");

  // Spokes jako "- /slug — title (hub_to_spoke)" (published) lub "- title (planned, brak URL)"
  // Format identyczny ze spokowym dla LLM (kontrakt zmiennych z Cowork v4 prompts).
  const spokesStr = ctx.cluster_member_spokes
    .map((s) => {
      if (s.is_published && s.wp_post_url) {
        const slugPath = extractSlugFromUrl(s.wp_post_url);
        if (slugPath) return `- ${slugPath} — ${s.title} (hub_to_spoke)`;
      }
      return `- ${s.title} (planned, brak URL)`;
    })
    .join("\n");

  return {
    macro_statement: ctx.macro_statement || "",
    seed_name: ctx.seed_name || "",
    seed_slug: ctx.seed_slug || "",
    cluster_member_entities: entitiesStr,
    cluster_member_slugs: spokesStr,
    sample_spoke_titles: ctx.sample_spoke_titles.join(", "),
  };
}

/**
 * PRE-STEP dla generatePillar() — utworzenie `thematic_node_relations` hub_to_spoke
 * dla wszystkich published spoków seeda. Graf + zakladka "Linki" + SRO audit
 * widza pillar w klastrze (koniec "izolowany node").
 *
 * Idempotent: DELETE existing hub_to_spoke FROM=pillar PRZED INSERT batch.
 * Zero zmian w spoke flow.
 */
export async function createHubToSpokeRelations(
  pillarNodeId: string,
  pillarMapId: string,
  pillarUserId: string,
  publishedSpokes: PillarSpokeMember[],
): Promise<{ created: number; deleted: number }> {
  // 1. Count + DELETE existing hub_to_spoke FROM=pillar (idempotent guard)
  const { count: existingCount } = await (supabase as any)
    .from("thematic_node_relations")
    .select("id", { count: "exact", head: true })
    .eq("from_node_id", pillarNodeId)
    .eq("relation_type", "hub_to_spoke");

  const deleted = existingCount ?? 0;

  if (deleted > 0) {
    const { error: deleteErr } = await (supabase as any)
      .from("thematic_node_relations")
      .delete()
      .eq("from_node_id", pillarNodeId)
      .eq("relation_type", "hub_to_spoke");

    if (deleteErr) {
      logDebug("PillarContext", `DELETE hub_to_spoke failed: ${deleteErr.message}`);
    }
  }

  // 2. INSERT batch — jeden row per published spoke (filter is_published + wp_post_url)
  const rowsToInsert = publishedSpokes
    .filter((s) => s.is_published && s.wp_post_url)
    .map((s) => ({
      user_id: pillarUserId,
      map_id: pillarMapId,
      from_node_id: pillarNodeId,
      to_node_id: s.node_id,
      relation_type: "hub_to_spoke",
      weight: 1,
    }));

  let created = 0;
  if (rowsToInsert.length > 0) {
    const { error: insertErr } = await (supabase as any)
      .from("thematic_node_relations")
      .insert(rowsToInsert);

    if (insertErr) {
      logDebug("PillarContext", `INSERT hub_to_spoke failed: ${insertErr.message}`);
    } else {
      created = rowsToInsert.length;
    }
  }

  logDebug(
    "PillarContext",
    `createHubToSpokeRelations: pillar=${pillarNodeId.slice(0, 8)} created=${created} deleted=${deleted}`,
  );

  return { created, deleted };
}

// isPillarFlowEnabled() removed in CLEANUP v1 #1 (2026-06-01) — dead code po usunieciu
// importu z index.ts. Zero callerów (grep-proven). V2 flow (pillarGenerator.ts) uzywa
// isPillarFlowV2Enabled() (osobny env flag VITE_PILLAR_FLOW_V2_ENABLED).
